import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { SearchWithPagination, createRequestOption } from '../utils/request-util';
import { Observable } from 'rxjs';
import { ProjectResponseDTO, ProjectRequestDTO, RefuseProjectDTO } from '../model/project.model';
import { GitlabProject } from '../model/gitlab.model';
import { ApplicationConfigService } from '../../core/config/application-config.service';
import { SessionQuery } from '../../core/state/session.query';
import { Authority } from '../../core/model/authority.model';
import { AppVersion } from '../model/appVersion.model';


@Injectable({ providedIn: 'root' })
export class ProjectService {
    private resourceUrl = "api/projects";
    private resourceAdminUrl = "api/admin/projects";

    constructor(
        private http: HttpClient,
        private sessionQuery: SessionQuery,
        private applicationConfigService: ApplicationConfigService
    ) { }


    query(isGitOps: boolean, req?: SearchWithPagination): Observable<HttpResponse<ProjectResponseDTO[]>> {
        const options = createRequestOption(req);
        let url = this.resourceUrl;
        if (isGitOps) {
            url = url.concat("/git-ops");
        }
        return this.http.get<ProjectResponseDTO[]>(`${this.applicationConfigService.getEndpointFor(url)}`, { params: options, observe: 'response' });
    }

    get(id: number): Observable<ProjectResponseDTO> {
        return this.http.get<ProjectResponseDTO>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}`);
    }

    save(projectToSave: ProjectRequestDTO): Observable<HttpResponse<ProjectResponseDTO>> {
        return this.http.post<ProjectResponseDTO>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}`, projectToSave, { observe: 'response' });
    }

    update(projectToUpdate: ProjectRequestDTO): Observable<HttpResponse<ProjectResponseDTO>> {
        return this.http.put<ProjectResponseDTO>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${projectToUpdate.id}`, projectToUpdate, { observe: 'response' });
    }

    delete(id: number): Observable<HttpResponse<void>> {
        return this.http.delete<void>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}`, { observe: 'response' });
    }

    askApprovalForProject(id: number): Observable<HttpResponse<void>> {
        return this.http.put<void>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}/ask-approval`, null, { observe: 'response' });
    }

    approveProject(id: number): Observable<HttpResponse<GitlabProject>> {
        if (this.sessionQuery.hasAnyAuthority(Authority.ROLE_ADMIN)) {
            return this.adminApproveProject(id);
        }

        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}/approve`, null, { observe: 'response' });
    }

    approveProjectAndAddToExistingGroup(idProject: number, idGroup: number): Observable<HttpResponse<GitlabProject>> {
        if (this.sessionQuery.hasAnyAuthority(Authority.ROLE_ADMIN)) {
            return this.adminApproveProjectAndAddToExistingGroup(idProject, idGroup);
        }

        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${idProject}/approve-for-group/${idGroup}`, null, { observe: 'response' });
    }

    refuseProject(id: number, msg: RefuseProjectDTO): Observable<HttpResponse<void>> {
        if (this.sessionQuery.hasAnyAuthority(Authority.ROLE_ADMIN)) {
            return this.adminRefuseProject(id, msg);
        }

        return this.http.put<void>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}/refuse`, msg, { observe: 'response' });
    }

    /** Jira 1026 
     * Gets the number waiting admin approval
    */
    getWaitingProjetsNumber(): Observable<HttpResponse<string>> {
        return this.http.get<string>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/count`, { observe: 'response' });
    }

    /** Jira 1289 
     * Gets the number of the project assigned to a referent, waiting for his approval
    */
    getDelegatedProjetsNumber(): Observable<HttpResponse<string>> {
        return this.http.get<string>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/delegated/count`, { observe: 'response' });
    }

    /** Jira 1289 
 *    Gets the number of the project assigned to a referent, waiting for his approval
    */
    adminCreateAndApproveCfgProjectAndAddToExistingGroup(idProject: number, idGroup: number, idPipeline: number,): Observable<HttpResponse<GitlabProject>> {
        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceAdminUrl)}/${idProject}/create-approve-cfg-for-group/${idGroup}/pipeline/${idPipeline}`, null, { observe: 'response' });
    }

     /** Jira 1555
 *    Update the project reference
    */
    adminUpdateProjectReference(projectId: number, userId: number): Observable<HttpResponse<GitlabProject>> {
        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceAdminUrl)}/${projectId}/update-project-reference/${userId}`, null, { observe: 'response' });
    }


    getApplicationVersion(): Observable<HttpResponse<AppVersion>> {
        return this.http.get<AppVersion>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/version`, { observe: 'response' });
    }

    archive(id: number, path: string): Observable<HttpResponse<ProjectResponseDTO>> {
        return this.http.post<ProjectResponseDTO>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/${id}/${path}`, null,{ observe: 'response' });
    }

    // unArchive(id: number): Observable<HttpResponse<ProjectResponseDTO>> {
    //     return this.http.post<ProjectResponseDTO>(`${this.applicationConfigService.getEndpointFor(this.resourceUrl)}/un-archive/${id}`, null,{ observe: 'response' });
    // }

    private adminApproveProject(id: number): Observable<HttpResponse<GitlabProject>> {
        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceAdminUrl)}/${id}/approve`, null, { observe: 'response' });
    }

    private adminApproveProjectAndAddToExistingGroup(idProject: number, idGroup: number): Observable<HttpResponse<GitlabProject>> {
        return this.http.put<GitlabProject>(`${this.applicationConfigService.getEndpointFor(this.resourceAdminUrl)}/${idProject}/approve-for-group/${idGroup}`, null, { observe: 'response' });
    }

    private adminRefuseProject(id: number, msg: RefuseProjectDTO): Observable<HttpResponse<void>> {
        return this.http.put<void>(`${this.applicationConfigService.getEndpointFor(this.resourceAdminUrl)}/${id}/refuse`, msg, { observe: 'response' });
    }
}